/**
 * Branded 404 — replaces the previous catch-all
 * `<Navigate to="/dashboard" />` which bounced strangers into a login
 * screen (a hostile first impression for anyone who clicks a stale link).
 *
 * Public, unauthenticated. Echoes the Login hero's dark purple gradient
 * + cropped star watermark so the brand feels continuous, even on the
 * page everyone hopes they never reach.
 */
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Home } from "lucide-react";
import { StarMark } from "@/components/Logo";

export default function NotFound() {
  const loc = useLocation();
  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#1a0a3a] via-[#2c0f5e] to-[#0b0b0c] px-6 text-white"
      data-testid="notfound-page"
    >
      <StarMark
        size={1100}
        className="pointer-events-none absolute -right-[440px] -top-[180px] text-ek-purple/22 select-none"
      />
      <Helmet>
        <title>Page not found — ERIO</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="relative z-10 max-w-xl text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ek-lilac">
          EKSELENS &middot; ERIO
        </p>
        <h1 className="mt-5 font-display text-[3.5rem] font-medium leading-none tracking-[-0.025em] text-white">
          404
        </h1>
        <p className="mt-4 text-lg font-light text-white/75">
          That page isn&apos;t here.
        </p>
        <p className="mt-2 text-sm font-light text-white/55">
          The URL you followed doesn&apos;t match anything on the ERIO platform —
          try the live demo, the public benchmarks, or sign in.
        </p>
        <p className="mt-3 font-mono text-[11px] text-white/30">{loc.pathname}</p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/how-it-works"
            className="inline-flex items-center gap-2 rounded-md bg-ek-purple px-4 py-2 text-sm font-semibold text-white hover:bg-ek-purple/90"
            data-testid="notfound-home-link">
            <Home size={14} /> How ERIO works
          </Link>
          <Link to="/benchmarks"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 px-4 py-2 text-sm font-medium text-white/85 hover:border-white hover:text-white"
            data-testid="notfound-benchmarks-link">
            Public benchmarks
          </Link>
          <Link to="/login"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 px-4 py-2 text-sm font-medium text-white/85 hover:border-white hover:text-white"
            data-testid="notfound-signin-link">
            <ArrowLeft size={14} /> Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
